/**
 * =============================================================================
 * Página: ReferralPrograms
 * Versão: 1.0.0 (Fase 5 — Bloco 5C, item 5.6)
 *
 * Descrição:
 *   Listagem e gestão dos programas de indicação do tenant ativo.
 *   - Cabeçalho compacto padronizado (memória ui/standardization/compact-header).
 *   - CRUD via modal `ReferralProgramFormModal`.
 *   - Confirmação de exclusão via AlertDialog.
 * =============================================================================
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Award, Plus, Pencil, Trash2, Link2, ExternalLink, Star } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useAutoOpenFromQuery } from '@/hooks/useAutoOpenFromQuery';
import { Button } from '@/components/ui/button';
import { ReferralQuotaInline } from '@/components/referral/ReferralQuotaInline';
import { ReferralUsageInline } from '@/components/referral/billing/ReferralUsageInline';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { StatusDot } from '@/components/tenant/SettingsInfoRow';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ScrollIndicators } from '@/components/ui/scroll-indicators';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import {
  useReferralPrograms,
  useReferralProgramMutations,
  type AffiliateProgram,
  type AffiliateProgramWithStats,
} from '@/hooks/useReferralPrograms';
import { ReferralProgramFormModal } from '@/components/referral/ReferralProgramFormModal';
import { useSetDefaultAffiliateProgram } from '@/hooks/useSetDefaultAffiliateProgram';

const PROGRAM_TYPE_LABEL: Record<string, string> = {
  invite_only: 'Privado',
  open: 'Público',
  internal: 'Interno',
};

const REWARD_LABEL: Record<string, string> = {
  percentage: 'Comissão %',
  fixed: 'Valor fixo',
  points: 'Pontos',
  recurring: 'Recorrente',
  tiered: 'Por tiers',
};

// Formatadores reutilizáveis (pt-BR)
const fmtInt = new Intl.NumberFormat('pt-BR');
const fmtBRL = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

export default function ReferralPrograms() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { currentTenant } = useAuth();
  const tenantSlug = currentTenant?.slug ?? '';
  const { data: programs = [], isLoading, isError } = useReferralPrograms();

  /**
   * Constrói a URL pública de cadastro de novos afiliados de um programa
   * `open`. Formato: <origin>/afiliado/cadastro/<tenantSlug>/<programSlug>
   */
  const buildPublicSignupUrl = (programSlug: string) => {
    const origin =
      typeof window !== 'undefined' ? window.location.origin : '';
    return `${origin}/afiliado/cadastro/${tenantSlug}/${programSlug}`;
  };

  const handleCopyPublicLink = async (programSlug: string) => {
    if (!tenantSlug) {
      toast({
        title: 'Slug do tenant não definido',
        description: 'Defina o slug do tenant em Configurações.',
        variant: 'destructive',
      });
      return;
    }
    try {
      await navigator.clipboard.writeText(buildPublicSignupUrl(programSlug));
      toast({ title: 'Link de cadastro copiado!' });
    } catch {
      toast({
        title: 'Não foi possível copiar o link',
        variant: 'destructive',
      });
    }
  };

  const handleOpenPublicLink = (programSlug: string) => {
    if (!tenantSlug) return;
    window.open(
      buildPublicSignupUrl(programSlug),
      '_blank',
      'noopener,noreferrer',
    );
  };
  const { deleteMutation } = useReferralProgramMutations();
  // Mutation para marcar um programa como o padrão do tenant (auto-cadastro).
  const setDefaultMutation = useSetDefaultAffiliateProgram();
  const [modalOpen, setModalOpen] = useState(false);
  const [selected, setSelected] = useState<AffiliateProgram | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<AffiliateProgram | null>(null);

  const handleEdit = (program: AffiliateProgram) => {
    setSelected(program);
    setModalOpen(true);
  };

  const handleNew = () => {
    setSelected(null);
    setModalOpen(true);
  };

  // Abre modal de novo programa via ?new=1 (Quick Create do header).
  useAutoOpenFromQuery('1', handleNew);

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return;
    try {
      await deleteMutation.mutateAsync(confirmDelete.id);
      toast({ title: 'Programa excluído com sucesso.' });
      setConfirmDelete(null);
    } catch (err: any) {
      toast({
        title: 'Erro ao excluir',
        description: err?.message ?? 'Tente novamente.',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="space-y-4">
      {/* Header compacto padronizado */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="h-9 w-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <Award className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h1 className="text-lg font-semibold truncate">Programas de Indicação</h1>
            <p className="text-xs text-muted-foreground truncate">
              Gerencie os programas que organizam suas campanhas e afiliados.
            </p>
          </div>
        </div>
        {/* Padrão TaskList: indicador acima, botão abaixo */}
        <div className="flex flex-col items-end gap-1.5 shrink-0">
          <ReferralUsageInline className="hidden md:inline-flex" />
          <ReferralQuotaInline resource="programs">
            <Button onClick={handleNew} size="xs" className="shrink-0">
              <Plus className="h-3.5 w-3.5" />
              Novo programa
            </Button>
          </ReferralQuotaInline>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-4 space-y-2">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : isError ? (
            <div className="p-6 text-sm text-destructive">
              Erro ao carregar programas. Tente recarregar a página.
            </div>
          ) : programs.length === 0 ? (
            <div className="p-10 text-center">
              <Award className="h-10 w-10 mx-auto text-muted-foreground/40 mb-2" />
              <p className="text-sm text-muted-foreground mb-4">
                Nenhum programa cadastrado ainda.
              </p>
              <Button onClick={handleNew} size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Criar primeiro programa
              </Button>
            </div>
          ) : (
            <ScrollIndicators>
            <Table scrollViewport className="min-w-[1300px]">
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead className="hidden md:table-cell">Tipo</TableHead>
                  <TableHead className="hidden xl:table-cell">Recompensa</TableHead>
                  <TableHead className="hidden 2xl:table-cell">Cookie</TableHead>
                  {/* Totais agregados (RPC get_referral_programs_with_stats) */}
                  <TableHead className="text-right hidden sm:table-cell">T. Afiliados</TableHead>
                  <TableHead className="text-right hidden lg:table-cell">T. Campanhas</TableHead>
                  <TableHead className="text-right hidden lg:table-cell">T. Indicações</TableHead>
                  <TableHead className="text-right hidden md:table-cell">T. Ganho</TableHead>
                  <TableHead className="text-right hidden md:table-cell">T. Pago</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {programs.map((p) => (
                  <TableRow
                    key={p.id}
                    className="cursor-pointer hover:bg-muted/40"
                    onClick={() =>
                      navigate(`/tenant/indicacao/programas/${p.id}?tab=dashboard`)
                    }
                  >
                    <TableCell>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium">{p.name}</span>
                        {/* Indicador "Padrão" — programa usado pelo link
                            genérico `/afiliado/<slug>/cadastro`. Estilo
                            sóbrio (outline neutro), sem cores chamativas. */}
                        {(p as any).is_default && (
                          <span
                            className="inline-flex items-center gap-1 rounded-md border border-border bg-muted/40 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground"
                            title="Programa padrão para o link público de cadastro do tenant"
                          >
                            <span className="h-1.5 w-1.5 rounded-full bg-foreground/70" />
                            Padrão
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">/{p.slug}</div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <Badge variant="outline">
                        {PROGRAM_TYPE_LABEL[p.program_type] ?? p.program_type}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden xl:table-cell">
                      {REWARD_LABEL[p.default_reward_type] ?? p.default_reward_type}
                      {' · '}
                      <span className="text-muted-foreground">
                        {p.default_reward_value}
                      </span>
                    </TableCell>
                    <TableCell className="hidden 2xl:table-cell text-muted-foreground">
                      {p.cookie_duration_days}d
                    </TableCell>
                    {/* T. Afiliados */}
                    <TableCell className="text-right tabular-nums hidden sm:table-cell">
                      {fmtInt.format(p.total_affiliates)}
                    </TableCell>
                    {/* T. Campanhas */}
                    <TableCell className="text-right tabular-nums hidden lg:table-cell">
                      {fmtInt.format(p.total_campaigns)}
                    </TableCell>
                    {/* T. Indicações */}
                    <TableCell className="text-right tabular-nums hidden lg:table-cell">
                      {fmtInt.format(p.total_referrals)}
                    </TableCell>
                    {/* T. Ganho */}
                    <TableCell className="text-right tabular-nums hidden md:table-cell">
                      {fmtBRL.format(p.total_earned)}
                    </TableCell>
                    {/* T. Pago */}
                    <TableCell className="text-right tabular-nums hidden md:table-cell">
                      {fmtBRL.format(p.total_paid)}
                    </TableCell>
                    {/* Status — padrão unificado (Badge outline + StatusDot) */}
                    <TableCell>
                      <Badge
                        variant="outline"
                        className="h-5 gap-1.5 px-1.5 text-[10px] font-medium uppercase tracking-wider"
                      >
                        <StatusDot tone={p.is_active ? 'emerald' : 'muted'} />
                        {p.is_active ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </TableCell>
                    <TableCell
                      className="text-right"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex justify-end gap-1">
                        {/* Link público de cadastro — apenas para programas "open" (cadastro aberto) */}
                        {p.program_type === 'open' && (
                          <>
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => handleCopyPublicLink(p.slug)}
                              disabled={!tenantSlug}
                              title={
                                tenantSlug
                                  ? 'Copiar link público de cadastro'
                                  : 'Defina o slug do tenant em Configurações'
                              }
                            >
                              <Link2 className="h-4 w-4" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => handleOpenPublicLink(p.slug)}
                              disabled={!tenantSlug}
                              title="Abrir página de cadastro em nova aba"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                        {/* Definir como padrão — programa usado pela rota
                            genérica `/afiliado/<slug>/cadastro`. Apenas
                            programas ATIVOS podem ser marcados. Visual
                            neutro: ícone outline, sem cor de destaque. */}
                        {!(p as any).is_default && (
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => setDefaultMutation.mutate(p.id)}
                            disabled={!p.is_active || setDefaultMutation.isPending}
                            title={
                              p.is_active
                                ? 'Definir como programa padrão (link público do tenant)'
                                : 'Programa inativo não pode ser padrão'
                            }
                          >
                            <Star className="h-4 w-4 text-muted-foreground" />
                          </Button>
                        )}
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => handleEdit(p)}
                          title="Editar"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => setConfirmDelete(p)}
                          title="Excluir"
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </ScrollIndicators>
          )}
        </CardContent>
      </Card>

      <ReferralProgramFormModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        program={selected}
      />

      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir programa?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. As campanhas vinculadas e indicações
              existentes podem ser bloqueadas pelo banco.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={deleteMutation.isPending}
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
